import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Icon } from "./icons";
import { LogLines } from "./LogLines";
import { Alert, Button, Input, LoadingBlock, Select } from "./ui";
import { useDatabaseLogs } from "../lib/queries";

/* The windows offered, by key. Their words are in `database.json` under
   `dbLogs`, keyed by the same value the query sends. */
const WINDOWS = ["30m", "2h", "24h", "7d", "all"] as const;

/**
 * Runtime logs for a database.
 *
 * Filtering happens on the server: `tail`, `since` and `search` are Dokploy's
 * own parameters on the per-engine log endpoints, so a busy database never
 * sends its whole history to be grepped here. The application endpoint takes
 * none of the three, which is why the app log viewer narrows client-side
 * instead — same job, opposite side of the wire.
 */
export function DatabaseLogs({
  orgId,
  databaseId,
}: {
  orgId: string | undefined;
  databaseId: string;
}) {
  const { t } = useTranslation("database");
  const [since, setSince] = useState("2h");
  const [tail, setTail] = useState(200);
  const [search, setSearch] = useState("");
  // Applied on submit rather than on every keystroke: each change is a round
  // trip to the deployment engine, which is not a place to send one per letter.
  const [applied, setApplied] = useState("");

  const logs = useDatabaseLogs(orgId, databaseId, {
    tail,
    since,
    ...(applied ? { search: applied } : {}),
  });

  return (
    <div className="space-y-3">
      <form
        className="flex flex-wrap items-end gap-2"
        onSubmit={(event) => {
          event.preventDefault();
          setApplied(search.trim());
        }}
      >
        <Select
          value={since}
          onChange={(e) => setSince(e.target.value)}
          className="w-auto"
          aria-label={t("dbLogs.window")}
        >
          {WINDOWS.map((value) => (
            <option key={value} value={value}>
              {t(`dbLogs.window_${value}`)}
            </option>
          ))}
        </Select>

        <Select
          value={String(tail)}
          onChange={(e) => setTail(Number(e.target.value))}
          className="w-auto"
          aria-label={t("dbLogs.lines")}
        >
          {[100, 200, 500, 1000].map((n) => (
            <option key={n} value={n}>
              {n} lines
            </option>
          ))}
        </Select>

        <div className="relative min-w-48 flex-1">
          <Icon
            name="search"
            className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-fg-subtle"
          />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t("dbLogs.filter")}
            className="pl-9"
            aria-label={t("dbLogs.filterLabel")}
          />
        </div>

        <Button type="submit" variant="secondary" loading={logs.isFetching}>
          {t("dbLogs.apply")}
        </Button>
      </form>

      {logs.isLoading ? (
        <LoadingBlock label={t("dbLogs.reading")} />
      ) : logs.data?.unavailable ? (
        <Alert tone="warning">
          {t("dbLogs.failed")}
        </Alert>
      ) : !logs.data?.logs ? (
        <p className="border border-line p-4 text-sm text-fg-subtle">
          {applied ? t("dbLogs.noMatch", { query: applied }) : t("dbLogs.empty")}
        </p>
      ) : (
        // The same pane the application viewer uses, so a database log and an
        // app log read identically — timestamp gutter, levels picked out, one
        // row per line. The toolbar above it differs because these filters run
        // on the server: `tail`, `since` and `search` are Dokploy's own
        // parameters, and narrowing in the browser would misreport what was
        // actually fetched.
        <div className="max-h-96 overflow-auto border border-line bg-canvas py-2">
          <LogLines lines={logs.data.logs.trimEnd().split("\n")} wrap />
        </div>
      )}
    </div>
  );
}
